/**
 * ArubaSitesPage — fleet-wide Aruba Central sites table.
 *
 * Read-only listing of every site reachable through the user's active key.
 * Each row shows the site name (with full address line built from the
 * address fields), country, device count, and tags. Search filters by
 * name + city + country + tags. KPIs at the top summarize fleet shape.
 *
 * Sites are physical-location anchors — different from groups, which are
 * logical config-template containers. Together they answer "where is my
 * stuff?" (sites) vs. "how is it configured?" (groups).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Search, MapPin, Building2, Globe2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  TableShell,
  TableHeadRow,
  TableRow,
  TableHeadCell,
  TableCell,
} from "@/components/ui/table-shell";
import {
  PanelSkeleton,
  Skeleton,
  TableSkeleton,
} from "@/components/ui/skeleton";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaSites } from "@/stores/aruba-sites";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

const COLS = "1.6fr 1.4fr 0.7fr 0.7fr 1fr";
const MAX_ROWS = 200;

/** Build the second-line address blob — joins non-empty parts with " · "
 *  so a site missing one component (e.g. no street address) doesn't
 *  render as "Cincinnati ·  · United States". */
function joinNonEmpty(
  parts: Array<string | null | undefined>,
  sep = " · ",
): string {
  return parts
    .flatMap((p) => {
      const s = (p ?? "").toString().trim();
      return s.length > 0 ? [s] : [];
    })
    .join(sep);
}

export function ArubaSitesPage() {
  const animatedRootRef = useUiAutoAnimate();

  const { data, status, error, load } = useArubaSites();
  useEffect(() => {
    void load();
  }, [load]);

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) => {
      const tagBlob = (s.tags ?? []).join(" ").toLowerCase();
      return (
        (s.site_name ?? "").toLowerCase().includes(q) ||
        (s.city ?? "").toLowerCase().includes(q) ||
        (s.state ?? "").toLowerCase().includes(q) ||
        (s.country ?? "").toLowerCase().includes(q) ||
        (s.address ?? "").toLowerCase().includes(q) ||
        tagBlob.includes(q)
      );
    });
  }, [data, query]);

  // KPIs — total sites, total countries, sum of device counts, sites
  // missing coordinates (a common data-quality gap on legacy tenants).
  const stats = useMemo(() => {
    const countries = new Set<string>();
    let totalDevices = 0;
    let withoutCoords = 0;
    for (const s of data) {
      if (s.country) countries.add(s.country);
      if (typeof s.associated_device_count === "number") {
        totalDevices += s.associated_device_count;
      }
      if (!s.latitude || !s.longitude) withoutCoords += 1;
    }
    return { countries: countries.size, totalDevices, withoutCoords };
  }, [data]);

  const visible = filtered.slice(0, MAX_ROWS);
  const overflow = filtered.length - visible.length;
  const isInitialLoading = status === "loading" && data.length === 0;

  if (isInitialLoading) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-14" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-12" />
        </div>

        <Skeleton className="h-10 w-44 rounded-lg" />

        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-[92%]" />
            <Skeleton className="h-4 w-[84%]" />
          </div>
          <Skeleton className="h-8 w-24 rounded-full" />
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>

        <Card>
          <CardContent className="flex items-center gap-3">
            <Skeleton className="h-10 max-w-md flex-1 rounded-xl" />
            <Skeleton className="ml-auto h-4 w-28" />
          </CardContent>
        </Card>

        <TableSkeleton columns={COLS} columnCount={5} rows={6} />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Sites</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Sites
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Physical locations registered in Aruba Central — addresses, device
            counts, and tags. Edit-in-place is managed in Central; this view is
            read-only.
          </p>
        </div>
        <Badge variant="info" size="md">
          {data.length} sites
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-rose/30 bg-rose/10 px-3.5 py-2.5 text-sm text-rose"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Unable to load sites.</b>{" "}
            <span className="text-rose/80">{error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiTile label="Sites" value={data.length} Icon={Building2} />
        <KpiTile label="Countries" value={stats.countries} Icon={Globe2} />
        <KpiTile label="Devices" value={stats.totalDevices} Icon={MapPin} />
        <KpiTile
          label="No coords"
          value={stats.withoutCoords}
          Icon={AlertTriangle}
          tone={stats.withoutCoords > 0 ? "warn" : undefined}
        />
      </div>

      <Card>
        <CardContent className="flex items-center gap-3">
          <div className="relative max-w-md flex-1">
            <Search
              size={14}
              className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, city, state, country, address, tag…"
              className="pl-9"
            />
          </div>
          <span className="ml-auto font-mono text-xs text-fg-mute">
            {filtered.length}/{data.length} match
            {overflow > 0 ? <> · capped to first {MAX_ROWS}</> : null}
          </span>
        </CardContent>
      </Card>

      {status === "loading" && data.length === 0 ? (
        <TableSkeleton columns={COLS} columnCount={5} rows={6} />
      ) : (
        <TableShell>
          <TableHeadRow columns={COLS}>
            <TableHeadCell>Name</TableHeadCell>
            <TableHeadCell>Address</TableHeadCell>
            <TableHeadCell>Country</TableHeadCell>
            <TableHeadCell className="text-right">Devices</TableHeadCell>
            <TableHeadCell>Tags</TableHeadCell>
          </TableHeadRow>
          {visible.map((s) => {
            // Coords aren't always usable strings — Aruba sometimes returns
            // them as 0/empty. Hide the pin icon when both are zero/missing.
            const hasCoords =
              !!s.latitude && !!s.longitude && s.latitude !== "0";
            const addressLine = joinNonEmpty([
              s.address,
              s.city,
              s.state,
              s.zipcode?.toString(),
            ]);
            return (
              <TableRow key={String(s.site_id ?? s.site_name)} columns={COLS}>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    {hasCoords ? (
                      <MapPin size={11} className="shrink-0 text-violet" />
                    ) : null}
                    <span className="truncate text-sm font-semibold text-fg">
                      {s.site_name ?? (
                        <span className="text-fg-mute">(unnamed)</span>
                      )}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="truncate text-xs text-fg-dim">
                  {addressLine || <span className="text-fg-mute">—</span>}
                </TableCell>
                <TableCell className="text-xs text-fg-dim">
                  {s.country ?? <span className="text-fg-mute">—</span>}
                </TableCell>
                <TableCell className="text-right font-mono text-xs text-fg">
                  {(s.associated_device_count ?? 0).toLocaleString()}
                </TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  {(s.tags ?? []).slice(0, 3).map((t) => (
                    <Badge key={t} variant="neutral" size="sm">
                      {t}
                    </Badge>
                  ))}
                  {(s.tags ?? []).length > 3 ? (
                    <Badge variant="neutral" size="sm">
                      +{(s.tags ?? []).length - 3}
                    </Badge>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-fg-mute">
              {query
                ? `No sites match "${query}".`
                : "No sites in this tenant."}
            </div>
          ) : null}
          {overflow > 0 ? (
            <div className="border-t border-line bg-surface-tint/40 px-4 py-2 text-center text-[11px] text-fg-mute">
              {overflow.toLocaleString()} more match — refine the filter to see
              them.
            </div>
          ) : null}
        </TableShell>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  Icon,
  tone,
}: {
  label: string;
  value: number;
  Icon: typeof MapPin;
  tone?: "warn";
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-3">
        <div
          className={
            tone === "warn"
              ? "flex h-9 w-9 items-center justify-center rounded-lg bg-peach/15 text-peach"
              : "flex h-9 w-9 items-center justify-center rounded-lg bg-aurora-soft text-violet"
          }
        >
          <Icon size={15} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            {label}
          </div>
          <div className="text-xl font-extrabold text-fg">
            {value.toLocaleString()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
